import { DialogContent, Grid, Stack, DialogTitle } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { isoCountries } from 'wisestep-api';
import {
  WsDialog,
  WsTypography,
  WsTypoVariants,
  WsButton,
  ControlledTextInput,
  WsSelect,
  WsSelectObj,
} from 'wisestep-base';

export interface IDialogWithInput {
  openDialog: boolean;
  closeDialog(): void;
  onInput: (data: FieldValues) => void;
  name: string;
  extraField?: boolean;
  addPlaceholder1?: string;
  addPlaceholder2?: string;
  isEdit?: boolean;
  defaultValue?: IDefaultValue;
  requireCountriesSelect?: boolean;
  gridXs?: number;
  isPaymentTypeComp?: boolean;
}

export interface IDefaultValue {
  fieldOne?: string;
  fieldTwo?: string;
}

const DialogWithInput = ({
  openDialog,
  closeDialog,
  onInput,
  name,
  extraField = false,
  addPlaceholder1 = 'Enter New Value',
  addPlaceholder2 = 'Enter New Value',
  isEdit = false,
  requireCountriesSelect = false,
  gridXs,
  defaultValue,
  isPaymentTypeComp = false,
}: IDialogWithInput) => {
  const methods = useForm({ defaultValues: defaultValue });
  const { reset } = methods;
  const [countries, setCountries] = useState<WsSelectObj[]>([]);

  useEffect(() => {
    const fetchCountries = () => {
      const countriesArray: WsSelectObj[] = Object.values(isoCountries).map((country) => ({
        label: country,
        value: country,
      }));

      setCountries(countriesArray);
    };
    requireCountriesSelect && fetchCountries();
  }, [requireCountriesSelect]);

  const onSubmit = (data: FieldValues) => {
    onInput(data);
    reset();
  };

  return (
    <FormProvider {...methods}>
      <WsDialog openDialog={openDialog} size="small">
        <DialogTitle>
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            {isEdit ? `Edit ${name}` : `Create ${name}`}
          </WsTypography>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={gridXs ?? (extraField ? 4 : 5)}>
                <ControlledTextInput
                  name="fieldOne"
                  size="medium"
                  placeholder={addPlaceholder1}
                  required
                  fullWidth
                />
              </Grid>
              {extraField && (
                <>
                  {isPaymentTypeComp ? (
                    <Grid item xs={12}>
                      <ControlledTextInput
                        name="fieldTwo"
                        size="medium"
                        placeholder={addPlaceholder2}
                        required
                        fullWidth
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <ControlledTextInput
                        name="fieldTwo"
                        size="small"
                        placeholder={addPlaceholder2}
                        required
                      />
                    </Grid>
                  )}
                </>
              )}
              {requireCountriesSelect && (
                <Grid item xs={5}>
                  <WsSelect
                    name="country"
                    selectArray={countries}
                    placeholder="select country"
                    showMandatorySymbol={true}
                    required
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <WsButton onClick={closeDialog} variant="outlined" size="large">
                    Close
                  </WsButton>
                  <WsButton type="submit" size="large">
                    Save
                  </WsButton>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </WsDialog>
    </FormProvider>
  );
};

export default DialogWithInput;
